/* classify.js — keyword-based filename classification and taxonomy validation. */
(function () {
  'use strict';
  const CAD = (window.CAD = window.CAD || {});

  const SENSITIVITIES = ['high', 'medium', 'low'];

  function stripExtension(name) {
    const i = name.lastIndexOf('.');
    return i > 0 ? name.slice(0, i) : name;
  }

  function findCategory(taxonomy, id) {
    const cats = (taxonomy && taxonomy.categories) || [];
    for (let i = 0; i < cats.length; i++) if (cats[i].id === id) return cats[i];
    return null;
  }

  /**
   * Classify one filename against the taxonomy.
   * v1 rule: case-insensitive substring match, first category in priorityOrder wins,
   * fallback category otherwise. Both the full name and the extension-stripped
   * name are tested so "invoice" matches "Q3_Invoice_Acme.pdf".
   */
  function classifyFilename(filename, taxonomy) {
    taxonomy = taxonomy || {};
    const cats = taxonomy.categories || [];
    const fallback =
      findCategory(taxonomy, taxonomy.matching && taxonomy.matching.fallbackCategoryId) ||
      cats[0] ||
      { id: 'uncategorized', defaultSensitivity: 'low' };

    const lower = String(filename || '').toLowerCase();
    const base = stripExtension(lower);

    const order =
      (taxonomy.matching && Array.isArray(taxonomy.matching.priorityOrder) && taxonomy.matching.priorityOrder.length)
        ? taxonomy.matching.priorityOrder
        : cats.map(function (c) { return c.id; });

    for (let oi = 0; oi < order.length; oi++) {
      const cat = findCategory(taxonomy, order[oi]);
      if (!cat || !Array.isArray(cat.keywords)) continue;
      for (let ki = 0; ki < cat.keywords.length; ki++) {
        const kw = String(cat.keywords[ki] || '').toLowerCase();
        if (!kw) continue;
        if (lower.indexOf(kw) !== -1 || base.indexOf(kw) !== -1) {
          return { categoryId: cat.id, sensitivity: cat.defaultSensitivity, matchedKeyword: cat.keywords[ki] };
        }
      }
    }
    return { categoryId: fallback.id, sensitivity: fallback.defaultSensitivity, matchedKeyword: null };
  }

  /** Re-run classification over every stored record. Never touches the network. */
  function reclassifyAll(taxonomy) {
    const files = CAD.storage.getFiles();
    const ids = Object.keys(files);
    for (let i = 0; i < ids.length; i++) {
      const rec = files[ids[i]];
      const cls = classifyFilename(rec.filename, taxonomy);
      rec.categoryId = cls.categoryId;
      rec.sensitivity = cls.sensitivity;
      rec.matchedKeyword = cls.matchedKeyword;
    }
    CAD.storage.saveFiles(files);
    return ids.length;
  }

  /** Validate a parsed taxonomy object. Returns { ok, errors } with specific messages. */
  function validateTaxonomy(obj) {
    const errors = [];
    const fail = function (msg) { errors.push(msg); };

    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
      return { ok: false, errors: ['Root must be a JSON object.'] };
    }
    if (!Array.isArray(obj.categories) || obj.categories.length === 0) {
      return { ok: false, errors: ['\u201Ccategories\u201D must be a non-empty array.'] };
    }

    const ids = new Set();
    obj.categories.forEach(function (cat, i) {
      const where = 'categories[' + i + ']' + (cat && cat.id ? ' (\u201C' + cat.id + '\u201D)' : '');
      if (!cat || typeof cat !== 'object') { fail(where + ' must be an object.'); return; }
      if (!cat.id || typeof cat.id !== 'string') fail(where + ' is missing a string \u201Cid\u201D.');
      else if (ids.has(cat.id)) fail('Duplicate category id \u201C' + cat.id + '\u201D.');
      else ids.add(cat.id);
      if (!cat.label || typeof cat.label !== 'string') fail(where + ' is missing a string \u201Clabel\u201D.');
      if (!cat.color || !/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(cat.color)) {
        fail(where + ' \u201Ccolor\u201D must be a hex string like \u201C#0EA5E9\u201D.');
      }
      if (SENSITIVITIES.indexOf(cat.defaultSensitivity) === -1) {
        fail(where + ' \u201CdefaultSensitivity\u201D must be one of high, medium, low (got ' + JSON.stringify(cat.defaultSensitivity) + ').');
      }
      if (!Array.isArray(cat.keywords)) fail(where + ' \u201Ckeywords\u201D must be an array.');
      else if (cat.keywords.some(function (k) { return typeof k !== 'string'; })) {
        fail(where + ' \u201Ckeywords\u201D must contain only strings.');
      }
    });

    if (!obj.matching || typeof obj.matching !== 'object') {
      fail('\u201Cmatching\u201D must be an object with a \u201CfallbackCategoryId\u201D.');
    } else {
      const fb = obj.matching.fallbackCategoryId;
      if (!fb || typeof fb !== 'string') fail('\u201Cmatching.fallbackCategoryId\u201D is required.');
      else if (!ids.has(fb)) fail('matching.fallbackCategoryId \u201C' + fb + '\u201D does not match any category id.');
      if (obj.matching.priorityOrder !== undefined) {
        if (!Array.isArray(obj.matching.priorityOrder)) {
          fail('\u201Cmatching.priorityOrder\u201D must be an array of category ids.');
        } else {
          obj.matching.priorityOrder.forEach(function (id) {
            if (!ids.has(id)) fail('matching.priorityOrder references unknown category id \u201C' + id + '\u201D.');
          });
        }
      }
    }

    return { ok: errors.length === 0, errors: errors };
  }

  CAD.classify = {
    classifyFilename: classifyFilename,
    reclassifyAll: reclassifyAll,
    validateTaxonomy: validateTaxonomy,
    SENSITIVITIES: SENSITIVITIES,
  };
})();
